import { useQuery } from '@tanstack/react-query';
import { useMetaIntegrationStatus, userProfileQueryOptions } from '@/lib/query-options';
import { derivePlatformConnections, type PlatformConnections } from '@/lib/platform-connections';

export type { PlatformConnections };

/**
 * Per-platform connection signals for the campaigns surface (#5937).
 *
 * One source of truth so the campaigns index, sidebar, detail panels and the creation wizard
 * all agree on what "connected" means per platform — the same derivations Settings >
 * Integrations uses (integration-settings.tsx), lifted out so campaign surfaces can gate
 * per-platform instead of walling the whole page behind Google (the #5937 bug).
 *
 * The actual semantics live in ``derivePlatformConnections`` (lib/platform-connections.ts),
 * kept pure so they are unit-tested; this hook only wires the queries in.
 */
export function usePlatformConnections(): PlatformConnections {
  // Meta is out of test mode (#6678): the status query runs for everyone, and the backend's
  // meta-integration-enabled flag refuses it with a 403 for an org it is off for, which reads as not connected.
  const { data: userProfile, isLoading: isProfileLoading } = useQuery(userProfileQueryOptions());
  const { data: metaStatus, isLoading: isMetaStatusLoading } = useMetaIntegrationStatus();

  return derivePlatformConnections({
    userProfile,
    isProfileLoading,
    metaStatus,
    isMetaStatusLoading,
  });
}
